import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../../../MainComponents/Hooks/useAxios.jsx";
import { useAuthState } from "../../../../MainComponents/GlobalContext.jsx";
import HelmetTags from "../../../../MainComponents/HelmetTags.jsx";
import Loader from "../../../../MainComponents/Loader.jsx";

function QuestionBankCategory({
  type,
  QuizQuestionsData,
  refetchQuiz,
  setRefetchQuiz,
  ID,
}) {
  const Modal = useRef(null);
  const allQuestionsRef = useRef(null);
  const { categoryID } = useParams();
  const navigate = useNavigate();
  const AuthState = useAuthState();

  const [deleteId, setDeleteId] = useState("");
  const [reFetch, setReFetch] = useState(false);
  const [submitDelete, setSubmitDelete] = useState({
    flag: "",
    dependency: false,
    data: {},
  });

  // --- API: fetch all questions ---
  const [allQuestionsData, allQuestionsErrors, loading] = useAxios(
    `${process.env.REACT_APP_CATEGORY_QUESTIONS_API}${categoryID}`,
    "GET",
    !type,
    reFetch
  );

  // --- API: delete question ---
  const [deleteQuestionSuccess] = useAxios(
    process.env.REACT_APP_DELETE_QUESTION_API,
    "POST",
    submitDelete.flag,
    submitDelete.dependency,
    submitDelete.data,
    true
  );

  // --- handle delete success ---
  useEffect(() => {
    if (deleteQuestionSuccess) {
      type ? setRefetchQuiz(!refetchQuiz) : setReFetch(!reFetch);
      setDeleteId("");
    }
  }, [deleteQuestionSuccess]);

  const finalData = useMemo(() => {
    if (QuizQuestionsData?.length > 0) return QuizQuestionsData;
    return allQuestionsData?.data || [];
  }, [QuizQuestionsData, allQuestionsData]);

  function handleDelete() {
    setSubmitDelete({
      flag: "deleteQuestion",
      dependency: !submitDelete.dependency,
      data: { id: deleteId },
    });
  }

  function canDelete() {
    const { admin } = AuthState.userData || {};
    return (
      admin?.roles?.[0]?.name === "superadmin" ||
      admin?.permissions?.some((p) =>
        [
          "questionbank_deletequestion",
          "lecture_quizzes_permissions",
          "lecture_homeworks_permissions",
        ].some((perm) => p.name.startsWith(perm))
      )
    );
  }

  function canEdit() {
    const { admin } = AuthState.userData || {};
    return (
      admin?.roles?.[0]?.name === "superadmin" ||
      admin?.permissions?.some((p) =>
        [
          "questionbank_editquestion",
          "lecture_quizzes_permissions",
          "lecture_homeworks_permissions",
        ].some((perm) => p.name.startsWith(perm))
      )
    );
  }

  function handleEdit(ques) {
    const basePath =
      type === "homework"
        ? `/admin/courses/homework/${ID}`
        : type === "quiz"
          ? `/admin/courses/quiz/${ID}`
          : `/admin/question-bank/${categoryID}`;

    const path = ques.is_essay
      ? `${basePath}/edit-essay-question/${ques.key}`
      : `${basePath}/edit-choose-question/${ques.key}`;

    navigate(path, {
      state: { lectureQuestion: type === "homework" || type === "quiz" },
    });
  }

  if (loading && !type) {
    return <Loader />;
  }

  return (
    <section
      className={`h-full w-full overflow-y-auto ${!type && "px-20"} py-20 dark:bg-dark dark:text-light sm:px-10`}
    >
      {!type && <HelmetTags title="Question Bank | Mr Mohamed Salama" />}

      {/* Delete Modal */}
      <div
        ref={Modal}
        className="modal hide absolute bottom-1/2 right-1/2 w-80 translate-x-1/2 translate-y-1/2 flex-col items-center gap-4 rounded-2xl bg-red-200 p-4"
      >
        <p className="mb-4 w-full text-center">
          Are you sure you want to delete?
        </p>
        <div className="flex items-center justify-center gap-5">
          <button
            onClick={() => {
              Modal.current.classList.add("hide");
              setDeleteId("");
            }}
            className="closeModal"
          >
            Close
          </button>
          <button onClick={handleDelete} className="confirmModal">
            Confirm
          </button>
        </div>
      </div>

      {!type && (
        <h2 className="mb-16 w-full text-end text-4xl font-bold sm:text-center">
          Questions
        </h2>
      )}

      <div
        ref={allQuestionsRef}
        className="all-questions flex h-auto w-full flex-col gap-14 px-10 amd:px-0"
      >
        {!type &&
          allQuestionsErrors &&
          typeof allQuestionsErrors === "string" && (
            <p className="w-full text-center">{allQuestionsErrors}</p>
          )}

        {/* Render Questions */}
        {finalData.map((ques) => (
          <div key={ques.key} className="singleQuestionWrapper">
            {/* Options (edit/delete) */}
            <div className="optionsQuestionWrapper">
              <div className="questionOptions">
                {canDelete() && (
                  <button
                    className="deleteQuestionBtn"
                    onClick={() => {
                      Modal.current.classList.remove("hide");
                      setDeleteId(ques.key);
                    }}
                  >
                    Delete
                  </button>
                )}
                {canEdit() && (
                  <button
                    className="editQuestionBtn"
                    onClick={() => handleEdit(ques)}
                  >
                    Edit
                  </button>
                )}
              </div>

              {/* Question details */}
              <div className="categoryQuestionWrapper">
                <div className="essayTitleWrapper">
                  {ques.difficulty && <h4>{ques.difficulty}</h4>}
                  {ques.is_essay === 1 && <span>Essay Question</span>}
                  <p>Question n. {ques.n}</p>
                </div>
                <div
                  className="categoryQuestion"
                  dangerouslySetInnerHTML={{ __html: ques.question || "" }}
                />
              </div>
            </div>

            {/* Answers (only for non-essay) */}
            {!ques.is_essay && (
              <div className="categoryAnswersWrapper">
                {["answer1", "answer2", "answer3", "answer4"].map(
                  (ansKey, index) => {
                    const ansHtml = ques[ansKey] || "";
                    if (!ansHtml.trim()) return null; // prevent showing undefined/empty

                    return (
                      <div
                        key={ansKey}
                        className={`categorySingleAnswer answer${index + 1}Wrapper ${
                          ques.correct_answer === ansKey
                            ? "correctSingleAnswer"
                            : ""
                        }`}
                      >
                        <p>Answer {index + 1}</p>
                        <div dangerouslySetInnerHTML={{ __html: ansHtml }} />
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

export default QuestionBankCategory;
